import React, { useState } from "react";
import cn from "classnames";
import styles from "./Settings.module.sass";
import ProfileInformation from "./ProfileInformation";
import FormationInformation from "./FormationInformation";
import CompanyInformation from "./CompanyInformation";
import Confirmation from "./Confirmation";
import { createDraftFolder, updateDraftFolder, updateFolderFormationData } from "../../services/folder.service";
import { updateStudent } from "../../services/student.service";
import { createCompany } from "../../services/company.service";

const FolderCreate = () => {
  // Configuration des étapes
  const steps = [
    {
      id: 0,
      title: "Étudiant",
      component: ProfileInformation,
    },
    {
      id: 1,
      title: "Formation",
      component: FormationInformation,
    },
    {
      id: 2,
      title: "Entreprise",
      component: CompanyInformation,
    },
    {
      id: 3,
      title: "Confirmation",
      component: Confirmation,
    },
  ];

  // États pour la navigation et les données
  const [currentStep, setCurrentStep] = useState(0);
  const [draftFolderId, setDraftFolderId] = useState(null);
  const [folderData, setFolderData] = useState({
    student: {},
    formation: null,
    promotion: null,
    dossierType: "AUTONOME",
    formationInfo: {},
    companyInfo: {},
    company: {},
    confirmation: {}
  });

  const progressionJson =
  {
    "ecole": {
      "cerfa_genere": false,
      "sign_cerfa": false,
      "sign_convention": false,
      "valide_cerfa": false
    },
    "entreprise": {
      "contrat_cree": false,
      "infos_remplies": false,
      "sign_cerfa": false,
      "sign_convention": false, 
      "tuteur_assigne": false
    },
    "etudiant": {
      "infos_remplies": false,
      "sign_cerfa": false       
    }
  }

  // Navigation entre les étapes
  const goToNextStep = async () => {
    if (currentStep < steps.length - 1) {
      // Si on passe de l'étape 0 (ProfileInformation) à l'étape 1, créer un brouillon
      if (currentStep === 0) {
        try {
          const draftData = {
            student: folderData.student,
            formation: folderData.formation,
            promotion: folderData.promotion,
            center: folderData.center,
            dossierType: folderData.dossierType,
            progressionJson: progressionJson
          };

          if (draftFolderId) {
            // Mettre à jour le brouillon existant
            await updateDraftFolder(draftFolderId, {
              student_id: draftData.student?.id || null,
              formation_id: draftData.formation || null,
              promotion_id: draftData.promotion || null,
              center_id: draftData.center?.id || null,
              progression: progressionJson
            });

            await updateStudent(draftData.student?.id, {
              promotion_id: draftData.promotion
            });
          } else {
            // Créer un nouveau brouillon
            const result = await createDraftFolder(draftData);
            if (result && result.folder) {
              setDraftFolderId(result.folder.id);
              console.log('Dossier et contrat créés:', result);
            } else {
              console.log('Échec de la création du brouillon');
            }
          }
        } catch (error) {
          console.error('Erreur lors de la sauvegarde du brouillon:', error);
        }
      }

      // Si on passe de l'étape 1 (FormationInformation) à l'étape 2, mettre à jour le brouillon avec les données de formation
      if (currentStep === 1 && draftFolderId) {
        try {
          if (folderData.formationInfo && Object.keys(folderData.formationInfo).length > 0) {
            await updateFolderFormationData(draftFolderId, folderData.formationInfo);
          } else {
            console.warn('Aucune donnée de formation à sauvegarder ou données vides');
          }
        } catch (error) {
          console.error('Erreur lors de la mise à jour du brouillon avec les données de formation:', error);
        }
      }

      // Si on passe de l'étape 2 (CompanyInformation) à l'étape 3, gérer l'entreprise
      if (currentStep === 2 && draftFolderId) {
        try {
          // Cas 1: Entreprise existante sélectionnée
          if (folderData.companyInfo?.selectedCompany) {
            await updateDraftFolder(draftFolderId, {
              company_id: folderData.companyInfo.selectedCompany.id
            });

            // Mettre à jour l'étudiant avec l'ID de l'entreprise
            await updateStudent(folderData.student.id, {
              company_id: folderData.companyInfo.selectedCompany.id
            });

            setFolderData(prev => ({
              ...prev,
              company: folderData.companyInfo.selectedCompany
            }));
          }
          // Cas 2: Création d'une nouvelle entreprise
          else if (folderData.companyInfo?.createCompany === true) {
            // Validation des données entreprise
            const requiredFields = ['company_name', 'representative_lastname', 'representative_firstname', 'representative_email'];
            const missingFields = requiredFields.filter(field => !folderData.companyInfo[field]);

            if (missingFields.length > 0) {
              throw new Error(`Champs manquants: ${missingFields.join(', ')}`);
            }

            const companyData = {
              name: folderData.companyInfo.company_name,
              representative_lastname: folderData.companyInfo.representative_lastname,
              representative_firstname: folderData.companyInfo.representative_firstname,
              email: folderData.companyInfo.representative_email,
              representative_email: folderData.companyInfo.representative_email,
              director_email: folderData.companyInfo.representative_email,
              director_first_name: folderData.companyInfo.representative_firstname,
              director_last_name: folderData.companyInfo.representative_lastname,
            };

            const result = await createCompany(companyData);
            if (result?.success && result?.company?.id) {
              // Mettre à jour le dossier avec l'ID de l'entreprise créée
              await updateDraftFolder(draftFolderId, {
                company_id: result.company.id
              });

              // Mettre à jour l'étudiant avec l'ID de l'entreprise
              await updateStudent(folderData.student.id, {
                company_id: result.company.id
              });

              setFolderData(prev => ({
                ...prev,
                company: result.company
              }));
            } else {
              throw new Error('Échec de la création de l\'entreprise');
            }
          }
        } catch (error) {
          console.error('Erreur lors de la gestion de l\'entreprise:', error);
          // Optionnel: afficher une notification d'erreur à l'utilisateur
          // mais continuer le flux pour ne pas bloquer
        }
      }

      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mise à jour des données du dossier
  const updateStepData = (stepKey, data) => {
    setFolderData(prev => ({
      ...prev,
      [stepKey]: typeof data === 'object' && data !== null ? { ...prev[stepKey], ...data } : data
    }));
  };

  // Sauvegarde finale du dossier
  const handleSaveDossier = () => {
    console.log("Données du dossier à sauvegarder:", folderData);
  };

  // Rendu du composant de l'étape actuelle
  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className={styles.settings}>
      {/* Navigation par étapes */}
      <div className={styles.menu}>
        {steps.map((step, index) => (
          <button
            className={cn(styles.button, {
              [styles.active]: currentStep === index,
              [styles.completed]: currentStep > index,
            })}
            key={step.id}
          >
            <span className={styles.stepNumber}>{index + 1}</span>
            {step.title}
          </button>
        ))}
      </div>

      {/* Contenu de l'étape actuelle */}
      <div className={styles.wrapper}>
        <div className={styles.stepContent}>
          <div className={styles.componentWrapper}>
            <CurrentStepComponent
              data={folderData}
              updateData={updateStepData}
              currentStep={currentStep}
            />
          </div>
        </div>

        {/* Boutons de navigation */}
        {currentStep < steps.length - 1 && (
          <div className={styles.navigationButtons}>
            <button
              className="button-stroke"
              onClick={goToPreviousStep}
              disabled={currentStep === 0}
            >
              Précédent
            </button>

            <button
              className="button"
              onClick={goToNextStep}
            >
              Suivant
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderCreate;
